import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  FlaskConical,
  Link2,
  Play,
  Trash2,
  XCircle,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import { useAuth } from '@shell/AuthContext';
import { AiutoCampo } from '../components/AiutoCampo';
import { VUOTO, capitalizza, fmtDate, fmtEuro } from '../lib/formato';

/**
 * Dettaglio di un contratto a canone: voci, periodi, rinnovo/disdetta e
 * annullamento.
 *
 * Alcuni accorgimenti deliberati.
 *
 * "Indietro" torna alla schermata di provenienza con navigate(-1), non a una
 * pagina fissa: chi arriva dall'elenco filtrato ritrova il suo filtro.
 *
 * Dopo un'azione su un periodo si aggiorna SOLO quella riga (o, per un
 * refetch completo come dopo rinnovo/disdetta/generazione, un flag
 * `ricaricando` che disabilita i pulsanti senza smontare la pagina): un
 * `loading` a piena pagina su OGNI ricarico farebbe perdere scroll e fuoco a
 * chi sta lavorando su un contratto lungo (review controllore 2026-08-03,
 * grave 1). La pagina si smonta per intero SOLO al primo caricamento
 * (`dati === null`).
 *
 * L'annullamento e' l'unica conferma esplicita fra i meccanismi NUOVI della
 * versione precedente di questa pagina (Dialog di ui_core, M7): registrazione
 * disdetta e conferma rinnovo non la chiedono. Lo scarto di un periodo
 * (contratti attivi) usa anche lui il Dialog (round 2, grave/punto 4
 * rifatto: campo motivo + conferma, niente piu' window.prompt) - cosi'
 * "l'utente ha rinunciato" (Annulla nel Dialog) e "il motivo non e' arrivato"
 * (submit senza motivo, validazione inline) sono distinguibili per
 * costruzione, non per un ramo di window.prompt che confondeva Annulla
 * utente e dialogo non mostrabile. La CONFERMA di un periodo (che emette una
 * fattura vera): il debito pre-esistente su window.confirm (dichiarato dalla
 * review controllore round 2) e' chiuso al Task 9 (C6, referto dali) - anche
 * lei ora e' un Dialog, stesso testo del referto.
 *
 * Formattatori (fmtDate, fmtEuro, capitalizza) importati da
 * `../lib/formato`, condivisi con ContrattiList e ContrattoNuovo: tre copie
 * locali erano il germe delle divergenze (review controllore round 2, medio
 * 7) - fmtDate in particolare compone la data dalle sue parti, mai
 * `new Date("YYYY-MM-DD")` che ECMA-262 interpreta come UTC (stessa classe
 * del bug di oggiISO qui sotto, round 2 medio 8).
 *
 * Task 11 (referto dali, emendato 2026-08-02, B3/B4/B5/I10/I11/M1-M8): il
 * riquadro "Rinnovo e disdetta" e la tabella periodi usano i dati del
 * dettaglio completo (Task 18) - rinnovo_auto, preavviso con la sua unita',
 * `ultimo_giorno_disdetta` gia' calcolato dal backend (mai un secondo
 * calcolo in JavaScript: un mese non e' 30 giorni, referto B3), disdette
 * registrate e la scadenza di ogni periodo. Sui contratti PASSIVI restano
 * nascosti Genera/Emetti/Scarta (B5): il backend li rifiuta comunque
 * (Task 18), e "Estendi le scadenze" e' a backlog (P5).
 *
 * Task 9 (referto dali 2026-08-09, DETTAGLIO b/c/d): il riquadro della
 * fiducia "Cosa succedera' da solo" (GET /stato-generazione + GET /anteprima,
 * interruttore per contratto senza dialogo, "Prova senza scrivere niente" ex
 * Simula spostata dentro), la catena periodi con colonne Periodo | Stato |
 * Importo | Fattura | Scadenza di incasso | Azioni (C5: link alla fattura,
 * mai "emessa" per una bozza), i permessi per azione (C7: puoGestire per
 * Genera/Scarta/interruttore/aggancio, puoConfermare per Emetti - stessi
 * permessi del backend, nessun pulsante che risponde 403) e l'aggancio di un
 * periodo gia' fatturato in passato (periodo dal calcolo, fattura dal
 * Combobox, mai una data digitata a mano).
 */

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  'Content-Type': 'application/json',
});

interface Riga {
  id: string;
  numero_linea: number;
  descrizione: string;
  unita_misura: string | null;
  origine_quantita: string;
  quantita: string | null;
  prezzo_unitario: string;
  sconto_percentuale: string | null;
  aliquota_iva_percentuale: string;
  attiva: boolean;
}

interface ScadenzaPeriodo {
  data: string;
  stato: string;
  importo: string;
}

interface Periodo {
  id: string;
  periodo_inizio: string;
  periodo_fine: string;
  stato: string;
  imponibile: string | null;
  iva: string | null;
  totale: string | null;
  // Il backend li manda gia' nel dettaglio (contratti.py dettaglio():
  // "fattura_id": p.fattura_id, "fattura_year": p.fattura_year): dichiararli
  // qui e' cio' che C5 chiedeva, per costruire il link alla fattura senza
  // un secondo giro di rete (referto dali, DETTAGLIO c).
  fattura_id: string | null;
  fattura_year: number | null;
  fattura_numero: string | null;
  note: string | null;
  ultimo_errore: string | null;
  scadenza: ScadenzaPeriodo | null;
}

interface Ricorrenza {
  attiva: boolean;
  periodicita: string;
  data_ancora: string;
  ancora_giorno: number;
  sorgente_quantita_codice: string;
  rinnovo_durata_mesi: number | null;
  pagamento_anticipato: boolean;
  ultima_generazione_at: string | null;
  errori_consecutivi: number;
  ultimo_errore: string | null;
}

interface Disdetta {
  id: string;
  scadenza_sorvegliata: string;
  data_invio: string;
  data_ricezione: string | null;
  esito: string;
  note: string | null;
}

interface Dettaglio {
  id: string;
  codice: string | null;
  tipo: string;
  stato: string;
  direzione: string;
  oggetto: string;
  ragione_sociale: string | null;
  data_inizio: string;
  data_fine: string | null;
  rinnovo_auto: boolean;
  preavviso_giorni: number | null;
  preavviso_mesi: number | null;
  registrato: boolean;
  estremi_registrazione: string | null;
  ultimo_giorno_disdetta: string | null;
  preavviso_testo: string | null;
  ricorrenza: Ricorrenza | null;
  righe: Riga[];
  periodi: Periodo[];
  disdette: Disdetta[];
}

interface Simulazione {
  stato: string;
  periodo_inizio: string | null;
  periodo_fine: string | null;
  totale: string | null;
  messaggio: string | null;
}

/** GET /{id}/stato-generazione (Task 9, P2): i TRE interruttori in serie -
 * ambiente, azienda (tenant), questo contratto - piu' cio' che il worker
 * vedrebbe al prossimo giro. E' la fonte unica per non dire mai "attiva" se
 * uno qualunque dei tre e' spento (referto dali, violazione P2). */
interface StatoGenerazione {
  ambiente: boolean;
  tenant: boolean;
  contratto: boolean;
  prossimo_fatturabile: string | null;
  canoni_scoperti: number;
  ultimo_errore: string | null;
}

/** GET /{id}/anteprima (P5): un elemento della lista "prossimi canoni",
 * calcolato sui dati SALVATI, PURO (nessuna scrittura). Tipizzata solo sui
 * campi che questa pagina usa: la risposta ne porta altri (iva, ritenuta,
 * netto_a_pagare, data_scadenza) che qui non servono. */
interface AnteprimaPeriodo {
  periodo_inizio: string;
  periodo_fine: string;
  totale: string;
}

/** Riga di GET /api/v1/fatture (solo attive): quanto serve al Combobox
 * dell'aggancio storico per farsi riconoscere una fattura reale. */
interface FatturaOpzione {
  id: string;
  year: number;
  numero: string | null;
  data: string;
  cessionario_denominazione: string | null;
  importo_totale: number | null;
}

/** Somma sicura di importi gia' arrotondati (stringhe "0.00"): niente
 * accumulo di errore in virgola mobile (HARD calcoli esatti al centesimo),
 * passando dai centesimi come interi. */
const sommaCentesimi = (importi: (string | null | undefined)[]): number =>
  importi.reduce((tot: number, v) => tot + Math.round(Number(v ?? '0') * 100), 0);

const centesimiInEuro = (centesimi: number): string =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(centesimi / 100);

/** Oggi in formato ISO (YYYY-MM-DD), composta dai componenti LOCALI
 * (getFullYear/getMonth/getDate): confrontabile lessicograficamente con le
 * date che arrivano dal backend. `toISOString()` converte a UTC, e in Italia
 * (UTC+1/+2) un istante dopo mezzanotte locale e' ancora il giorno PRIMA in
 * UTC: usarlo darebbe "oggi" sbagliato di un giorno proprio nella finestra
 * serale/notturna - stessa classe del bug di stanotte (review controllore
 * 2026-08-03, grave 11). */
const oggiISO = () => {
  const d = new Date();
  const mese = String(d.getMonth() + 1).padStart(2, '0');
  const giorno = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mese}-${giorno}`;
};

/** Quanti giorni separano oggi da una data ISO (YYYY-MM-DD): positivo se
 * futura, negativo se passata. Confronto per differenza di giorni di
 * calendario, non di millisecondi: costruendo entrambe le date a mezzanotte
 * locale non serve nessuna conversione di fuso. */
const giorniAlTermine = (dataIso: string): number => {
  const [annoO, meseO, giornoO] = oggiISO().split('-').map(Number);
  const [annoT, meseT, giornoT] = dataIso.split('-').map(Number);
  const oggi = new Date(annoO, meseO - 1, giornoO);
  const termine = new Date(annoT, meseT - 1, giornoT);
  return Math.round((termine.getTime() - oggi.getTime()) / 86_400_000);
};

/** Il termine di disdetta dice se e' gia' passato (rinnovo tacito ormai
 * avvenuto) o quanto manca: una data nuda non risponde alla domanda che il
 * riquadro esiste per rispondere (review controllore 2026-08-03, medio 12). */
const descrizioneTermineDisdetta = (dataIso: string): string => {
  const giorni = giorniAlTermine(dataIso);
  const data = fmtDate(dataIso);
  if (giorni < 0) return `Termine scaduto il ${data}`;
  if (giorni === 0) return `Termine oggi, ${data}`;
  return `${data} (${giorni === 1 ? 'manca 1 giorno' : `mancano ${giorni} giorni`})`;
};

// Microcopy esatta del referto dali (mai "proposto"/"confermato"/"scartato"
// nudi: sono nomi di stato, non parole per un umano). 'fallito', 'in_corso'
// e 'saltato' non hanno una riga dedicata nella tabella del referto - sugli
// attivi 'in_corso' non dovrebbe comparire mai (e' lo stato che apre solo
// l'apertura passiva), restano etichette neutre di ripiego.
const STATO_PERIODO_LABEL: Record<string, string> = {
  proposto: 'Pronto da fatturare',
  confermato: 'Fatturato',
  scartato: 'Non fatturato',
  fallito: 'Fallito',
  in_corso: 'In corso',
  saltato: 'Saltato',
};
const STATO_PERIODO_VARIANT: Record<
  string,
  'default' | 'success' | 'muted' | 'destructive' | 'warning'
> = {
  proposto: 'default',
  confermato: 'success',
  scartato: 'muted',
  fallito: 'destructive',
  in_corso: 'warning',
  saltato: 'muted',
};

/** FastAPI manda `detail` come stringa o come array di errori di validazione
 * (loc + msg): senza normalizzare si legge "[object Object]" (referto dali I2). */
const normalizzaErrore = (dati: unknown, status: number, ripiego: string): string => {
  const detail = (dati as { detail?: unknown } | null)?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parti = detail.map((e) => {
      const errore = e as { loc?: unknown[]; msg?: string };
      const loc = Array.isArray(errore.loc)
        ? errore.loc.filter((x) => x !== 'body').join('.')
        : '';
      return loc ? `${loc}: ${errore.msg ?? ''}` : (errore.msg ?? JSON.stringify(errore));
    });
    if (parti.length) return parti.join('; ');
  }
  return ripiego || `Errore (${status})`;
};

const leggiErrore = async (res: Response, ripiego: string) => {
  const dati = await res.json().catch(() => null);
  return normalizzaErrore(dati, res.status, ripiego);
};

const ContrattoDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermesso } = useAuth();
  const puoGestire = hasPermesso('contratti.gestione');
  // C7: Emetti la fattura e' l'UNICO punto che consuma un numero fiscale,
  // ed e' l'unica azione dietro un permesso diverso da contratti.gestione
  // (contratti.py:406, require_permission("contratti.conferma")).
  const puoConfermare = hasPermesso('contratti.conferma');

  // Lo state di arrivo (dalla creazione o, dopo l'annullamento, dall'elenco
  // stesso in senso inverso) porta due cose distinte: `esito` (un messaggio
  // da mostrare una volta sola) ed `elencoSearch` (la query string
  // dell'elenco di provenienza, per tornarci CONSERVANDO i filtri dopo
  // un'azione distruttiva - review controllore 2026-08-03, grave 5).
  const statoIngresso = location.state as { esito?: string; elencoPath?: string; elencoSearch?: string } | null;
  const baseLista = location.pathname.startsWith('/vendite/contratti')
    ? '/vendite/contratti'
    : location.pathname.startsWith('/acquisti/contratti')
      ? '/acquisti/contratti'
      : '/contratti';
  const tornaElenco = (replace = false) => {
    const elencoPath = statoIngresso?.elencoPath ?? baseLista;
    const elencoSearch = statoIngresso?.elencoSearch ?? '';
    navigate(`${elencoPath}${elencoSearch}`, replace ? { replace: true } : undefined);
  };

  const [dati, setDati] = useState<Dettaglio | null>(null);
  // Full-page SOLO al primo caricamento (dati === null): un refetch dopo
  // rinnovo/disdetta/generazione non deve smontare la pagina, o scroll e
  // fuoco si perdono (review controllore, grave 1). `ricaricando` disabilita
  // i pulsanti durante un refetch senza nascondere cio' che e' gia' a
  // schermo.
  const [ricaricando, setRicaricando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [avviso, setAvviso] = useState<string | null>(statoIngresso?.esito ?? null);
  const [simulazione, setSimulazione] = useState<Simulazione | null>(null);
  const [inCorso, setInCorso] = useState<string | null>(null);

  // L'esito arriva una volta sola: ripulire subito lo state della history,
  // altrimenti un F5 lo ripropone.
  useEffect(() => {
    if (statoIngresso?.esito) {
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carica = useCallback(async () => {
    if (!id) return;
    setRicaricando(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/contratti/${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Caricamento contratto non riuscito (${res.status})`);
      setDati(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore imprevisto');
    } finally {
      setRicaricando(false);
    }
  }, [id]);

  useEffect(() => {
    void carica();
  }, [carica]);

  // --- Il riquadro della fiducia (Task 9): stato-generazione + anteprima ----
  // Solo sui contratti ATTIVI (B5): il worker non tocca i passivi
  // (generazione_auto.esegui_giro_tenant filtra direzione='attivo'), quindi
  // per un passivo i tre interruttori non significherebbero niente - meglio
  // non chiederli che mostrare un dato vero ma fuori luogo.

  const [statoGen, setStatoGen] = useState<StatoGenerazione | null>(null);
  const [erroreStatoGen, setErroreStatoGen] = useState<string | null>(null);

  const caricaStatoGen = useCallback(async () => {
    if (!id) return;
    try {
      const res = await fetch(`/api/v1/contratti/${id}/stato-generazione`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Stato della generazione non disponibile (${res.status})`);
      setStatoGen(await res.json());
      setErroreStatoGen(null);
    } catch (err) {
      setErroreStatoGen(err instanceof Error ? err.message : 'Errore imprevisto');
    }
  }, [id]);

  const [anteprimaPeriodi, setAnteprimaPeriodi] = useState<AnteprimaPeriodo[] | null>(null);
  // Contratto "a consumo" (origine_quantita='rilevata') senza rilevazioni:
  // GET /anteprima risponde 422 (piano.QuantitaMancante). Non e' un errore,
  // e' un caso previsto (Task 9): niente riquadro rosso, un messaggio in
  // parole povere al posto della tabella.
  const [anteprimaAConsumo, setAnteprimaAConsumo] = useState(false);
  const [erroreAnteprima, setErroreAnteprima] = useState<string | null>(null);

  const caricaAnteprima = useCallback(async () => {
    if (!id) return;
    try {
      const res = await fetch(`/api/v1/contratti/${id}/anteprima`, { headers: authHeaders() });
      if (res.status === 422) {
        setAnteprimaPeriodi(null);
        setAnteprimaAConsumo(true);
        setErroreAnteprima(null);
        return;
      }
      if (!res.ok) throw new Error(`Anteprima non disponibile (${res.status})`);
      const corpo = await res.json();
      setAnteprimaPeriodi(corpo.periodi ?? []);
      setAnteprimaAConsumo(false);
      setErroreAnteprima(null);
    } catch (err) {
      setAnteprimaPeriodi(null);
      setAnteprimaAConsumo(false);
      setErroreAnteprima(err instanceof Error ? err.message : 'Errore imprevisto');
    }
  }, [id]);

  useEffect(() => {
    if (!dati || dati.direzione !== 'attivo') return;
    void caricaStatoGen();
    void caricaAnteprima();
    // dati?.direzione non cambia mai dopo la creazione: questo effetto parte
    // una volta sola al primo caricamento. Le azioni che cambiano scoperti/
    // anteprima (genera, emetti, scarta, aggancia, interruttore) richiamano
    // caricaStatoGen/caricaAnteprima da sole, non serve riascoltare `dati`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dati?.direzione, caricaStatoGen, caricaAnteprima]);

  // --- Interruttore per contratto: PATCH ricorrenza_attiva, NESSUN dialogo
  // (referto dali, DETTAGLIO b) -----------------------------------------

  const [alternandoRicorrenza, setAlternandoRicorrenza] = useState(false);
  const [erroreRicorrenza, setErroreRicorrenza] = useState<string | null>(null);

  const alternaRicorrenza = async () => {
    if (!id || !dati?.ricorrenza) return;
    setAlternandoRicorrenza(true);
    setErroreRicorrenza(null);
    try {
      const res = await fetch(`/api/v1/contratti/${id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ ricorrenza_attiva: !dati.ricorrenza.attiva }),
      });
      if (!res.ok) throw new Error(await leggiErrore(res, 'Aggiornamento non riuscito'));
      await carica();
    } catch (err) {
      setErroreRicorrenza(err instanceof Error ? err.message : 'Errore imprevisto');
    } finally {
      setAlternandoRicorrenza(false);
    }
  };

  // --- Simula / Genera / Conferma / Scarta (solo contratti ATTIVI, B5) ------

  const simula = async () => {
    if (!id) return;
    setInCorso('simulazione');
    setError(null);
    setAvviso(null);
    try {
      const res = await fetch(`/api/v1/contratti/${id}/genera?dry_run=true`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(await leggiErrore(res, 'Simulazione non riuscita'));
      setSimulazione(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore imprevisto');
    } finally {
      setInCorso(null);
    }
  };

  const genera = async () => {
    if (!id) return;
    setInCorso('generazione');
    setError(null);
    setSimulazione(null);
    try {
      const res = await fetch(`/api/v1/contratti/${id}/genera`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(await leggiErrore(res, 'Generazione non riuscita'));
      const esito = await res.json();
      setAvviso(
        esito.stato === 'proposto'
          ? `Proposta creata per il periodo ${fmtDate(esito.periodo_inizio)} - ${fmtDate(esito.periodo_fine)}.`
          : `Nessuna proposta: ${esito.messaggio ?? esito.stato}.`,
      );
      // Qui il ricarico serve: e' comparsa una riga nuova. Cambia anche
      // canoni_scoperti e i "prossimi canoni": il periodo appena proposto
      // esce dall'anteprima (che salta ogni periodo gia' presente).
      await Promise.all([carica(), caricaStatoGen(), caricaAnteprima()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore imprevisto');
    } finally {
      setInCorso(null);
    }
  };

  // --- Emetti la fattura: Dialog di ui_core, non window.confirm (C6). Il
  // debito pre-esistente (review controllore round 2) e' chiuso: il testo e'
  // quello del referto dali, e il Dialog e' l'unico posto che mostra
  // l'errore di questa azione (niente piu' un badge d'errore per-riga: quel
  // canale serviva solo al vecchio window.confirm). ---------------------

  const [periodoDaEmettere, setPeriodoDaEmettere] = useState<Periodo | null>(null);
  const [erroreEmetti, setErroreEmetti] = useState<string | null>(null);

  const apriEmetti = (periodo: Periodo) => {
    setPeriodoDaEmettere(periodo);
    setErroreEmetti(null);
  };

  const confermaEmissione = async () => {
    if (!periodoDaEmettere) return;
    const periodo = periodoDaEmettere;
    setInCorso(periodo.id);
    setErroreEmetti(null);
    try {
      const res = await fetch(`/api/v1/contratti/periodi/${periodo.id}/conferma`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(await leggiErrore(res, 'Emissione non riuscita'));
      const esito = await res.json();
      setAvviso(`Fattura ${esito.fattura_numero} emessa in bozza.`);
      setPeriodoDaEmettere(null);
      // La conferma crea/aggiorna anche la scadenza di incasso del periodo
      // (backend, Task 9): non e' nella risposta di questo endpoint, serve
      // il dettaglio completo - stesso motivo di genera() qui sopra.
      await Promise.all([carica(), caricaStatoGen(), caricaAnteprima()]);
    } catch (err) {
      setErroreEmetti(err instanceof Error ? err.message : 'Errore imprevisto');
    } finally {
      setInCorso(null);
    }
  };

  const aggiornaRiga = (periodo_id: string, modifiche: Partial<Periodo>) => {
    setDati((precedente) =>
      precedente == null
        ? precedente
        : {
            ...precedente,
            periodi: precedente.periodi.map((p) =>
              p.id === periodo_id ? { ...p, ...modifiche } : p,
            ),
          },
    );
  };

  // --- Scarta un periodo: Dialog di ui_core, non window.prompt (round 2,
  // punto 4 rifatto). window.prompt confondeva due casi che il Dialog
  // distingue per costruzione: "l'utente ha rinunciato" (Annulla nel
  // Dialog, nessun errore) e "il motivo non e' arrivato" (submit col campo
  // vuoto, validazione inline, non un'eccezione).

  const [periodoDaScartare, setPeriodoDaScartare] = useState<Periodo | null>(null);
  const [scartaMotivo, setScartaMotivo] = useState('');
  const [erroreScarta, setErroreScarta] = useState<string | null>(null);

  const apriScarta = (periodo: Periodo) => {
    setPeriodoDaScartare(periodo);
    setScartaMotivo('');
    setErroreScarta(null);
  };

  const confermaScarta = async () => {
    if (!periodoDaScartare) return;
    if (!scartaMotivo.trim()) {
      setErroreScarta('Il motivo e\' obbligatorio: resta scritto sul contratto.');
      return;
    }
    const periodo = periodoDaScartare;
    const motivo = scartaMotivo.trim();
    setInCorso(periodo.id);
    setErroreScarta(null);
    try {
      const res = await fetch(`/api/v1/contratti/periodi/${periodo.id}/scarta`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ motivo }),
      });
      if (!res.ok) throw new Error(await leggiErrore(res, 'Scarto non riuscito'));
      aggiornaRiga(periodo.id, { stato: 'scartato', note: motivo });
      setPeriodoDaScartare(null);
    } catch (err) {
      setErroreScarta(err instanceof Error ? err.message : 'Errore imprevisto');
    } finally {
      setInCorso(null);
    }
  };

  // --- Conferma rinnovo -----------------------------------------------------

  const [erroreRinnovo, setErroreRinnovo] = useState<string | null>(null);

  const confermaRinnovo = async () => {
    if (!id) return;
    setInCorso('rinnovo');
    setErroreRinnovo(null);
    try {
      const res = await fetch(`/api/v1/contratti/${id}/conferma-rinnovo`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(await leggiErrore(res, 'Conferma del rinnovo non riuscita'));
      const esito = await res.json();
      setAvviso(`Rinnovo confermato: nuova scadenza ${fmtDate(esito.data_fine)}.`);
      await carica();
    } catch (err) {
      setErroreRinnovo(err instanceof Error ? err.message : 'Errore imprevisto');
    } finally {
      setInCorso(null);
    }
  };

  // --- "Questo periodo l'abbiamo gia' fatturato" (aggancia-storico): il
  // periodo si sceglie dal calcolo (anteprimaPeriodi), MAI da una data
  // digitata a mano - referto dali, DETTAGLIO d. La fattura si cerca col
  // Combobox server-search, stesso pattern di ContrattoNuovo.tsx per la
  // controparte (debounce 250ms + guardia anti-sorpasso). --------------

  const [aggancioOpen, setAggancioOpen] = useState(false);
  const [periodoAggancio, setPeriodoAggancio] = useState<AnteprimaPeriodo | null>(null);
  const [fatturaQuery, setFatturaQuery] = useState('');
  const [fatturaOpzioni, setFatturaOpzioni] = useState<FatturaOpzione[]>([]);
  const [fatturaSelezionata, setFatturaSelezionata] = useState<FatturaOpzione | null>(null);
  const [cercandoFatture, setCercandoFatture] = useState(false);
  const [erroreFatture, setErroreFatture] = useState<string | null>(null);
  const [erroreAggancio, setErroreAggancio] = useState<string | null>(null);
  const [salvandoAggancio, setSalvandoAggancio] = useState(false);

  const apriAggancio = () => {
    const primo = anteprimaPeriodi && anteprimaPeriodi.length > 0 ? anteprimaPeriodi[0] : null;
    setPeriodoAggancio(primo);
    setFatturaQuery('');
    setFatturaSelezionata(null);
    setFatturaOpzioni([]);
    setErroreFatture(null);
    setErroreAggancio(null);
    setAggancioOpen(true);
  };

  // La fattura da agganciare e' quasi sempre di un anno diverso da quello in
  // corso (e' storia pregressa): year si deriva dal periodo scelto, non e'
  // un filtro libero. Riparte a ogni cambio di periodo o di testo digitato,
  // MAI se una fattura e' gia' selezionata (l'input mostra il suo numero).
  const fatturaGenerazioneRef = useRef(0);
  useEffect(() => {
    if (!aggancioOpen || !periodoAggancio || fatturaSelezionata) return;
    const q = fatturaQuery.trim();
    const anno = Number(periodoAggancio.periodo_fine.slice(0, 4));
    const timer = setTimeout(async () => {
      fatturaGenerazioneRef.current += 1;
      const generazione = fatturaGenerazioneRef.current;
      setCercandoFatture(true);
      setErroreFatture(null);
      try {
        const parametri = new URLSearchParams({
          direzione: 'attiva', year: String(anno), limit: '20',
        });
        if (q) parametri.set('q', q);
        const res = await fetch(`/api/v1/fatture?${parametri.toString()}`, {
          headers: authHeaders(),
        });
        if (fatturaGenerazioneRef.current !== generazione) return;
        if (!res.ok) {
          setErroreFatture(`Ricerca non riuscita (${res.status})`);
          setFatturaOpzioni([]);
          return;
        }
        setFatturaOpzioni(await res.json());
      } catch {
        if (fatturaGenerazioneRef.current === generazione) {
          setErroreFatture('Ricerca non riuscita: errore di rete.');
          setFatturaOpzioni([]);
        }
      } finally {
        if (fatturaGenerazioneRef.current === generazione) setCercandoFatture(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [aggancioOpen, periodoAggancio, fatturaQuery, fatturaSelezionata]);

  const confermaAggancio = async () => {
    if (!id || !periodoAggancio || !fatturaSelezionata) {
      setErroreAggancio('Scegli il periodo e la fattura.');
      return;
    }
    setSalvandoAggancio(true);
    setErroreAggancio(null);
    try {
      const res = await fetch(`/api/v1/contratti/${id}/aggancia-storico`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          periodo_inizio: periodoAggancio.periodo_inizio,
          periodo_fine: periodoAggancio.periodo_fine,
          fattura_id: fatturaSelezionata.id,
          fattura_year: fatturaSelezionata.year,
        }),
      });
      if (!res.ok) throw new Error(await leggiErrore(res, 'Aggancio non riuscito'));
      setAggancioOpen(false);
      setAvviso(
        `Periodo ${fmtDate(periodoAggancio.periodo_inizio)} - ` +
          `${fmtDate(periodoAggancio.periodo_fine)} agganciato alla fattura.`,
      );
      await Promise.all([carica(), caricaStatoGen(), caricaAnteprima()]);
    } catch (err) {
      setErroreAggancio(err instanceof Error ? err.message : 'Errore imprevisto');
    } finally {
      setSalvandoAggancio(false);
    }
  };

  // --- Registra la disdetta (I11: niente PEC, I10: seconda volta -> etichetta) --

  const [disdettaApertaOpen, setDisdettaApertaOpen] = useState(false);
  const [disdettaDataInvio, setDisdettaDataInvio] = useState('');
  const [disdettaDataRicezione, setDisdettaDataRicezione] = useState('');
  const [disdettaNote, setDisdettaNote] = useState('');
  const [erroreDisdetta, setErroreDisdetta] = useState<string | null>(null);
  const [salvandoDisdetta, setSalvandoDisdetta] = useState(false);

  const apriDisdetta = () => {
    setDisdettaDataInvio('');
    setDisdettaDataRicezione('');
    setDisdettaNote('');
    setErroreDisdetta(null);
    setDisdettaApertaOpen(true);
  };

  const registraDisdetta = async () => {
    if (!id || !disdettaDataInvio) {
      setErroreDisdetta('La data di invio e\' obbligatoria.');
      return;
    }
    setSalvandoDisdetta(true);
    setErroreDisdetta(null);
    try {
      const res = await fetch(`/api/v1/contratti/${id}/disdetta`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          data_invio: disdettaDataInvio,
          data_ricezione: disdettaDataRicezione || null,
          note: disdettaNote.trim() || null,
        }),
      });
      if (!res.ok) throw new Error(await leggiErrore(res, 'Registrazione della disdetta non riuscita'));
      setDisdettaApertaOpen(false);
      setAvviso(`Disdetta registrata il ${fmtDate(disdettaDataInvio)}.`);
      await carica();
    } catch (err) {
      setErroreDisdetta(err instanceof Error ? err.message : 'Errore imprevisto');
    } finally {
      setSalvandoDisdetta(false);
    }
  };

  // --- Annulla il contratto (unica conferma esplicita, M7/M8) ---------------

  const [annullaApertoOpen, setAnnullaApertoOpen] = useState(false);
  const [erroreAnnulla, setErroreAnnulla] = useState<string | null>(null);
  const [annullando, setAnnullando] = useState(false);

  const annullaContratto = async () => {
    if (!id) return;
    setAnnullando(true);
    setErroreAnnulla(null);
    try {
      const res = await fetch(`/api/v1/contratti/${id}/annulla`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(await leggiErrore(res, 'Annullamento non riuscito'));
      // Torna all'elenco CONSERVANDO i filtri di provenienza (elencoSearch,
      // se l'arrivo e' stato da li') e porta l'esito con state, come la
      // creazione: navigate(-1) da solo si romperebbe se non si viene
      // dall'elenco (review controllore 2026-08-03, grave 5). replace:true
      // (round 2, grave 2): senza, il Back del browser dal dettaglio del
      // contratto ormai annullato riportava a un dettaglio morto invece che
      // all'elenco.
      const elencoSearch = statoIngresso?.elencoSearch ?? '';
      navigate(`${statoIngresso?.elencoPath ?? baseLista}${elencoSearch}`, {
        state: { esito: 'Contratto annullato.' },
        replace: true,
      });
    } catch (err) {
      setErroreAnnulla(err instanceof Error ? err.message : 'Errore imprevisto');
    } finally {
      setAnnullando(false);
    }
  };

  // Full-page SOLO quando non c'e' ancora niente da mostrare (dati === null):
  // il primo caricamento, o il primo fallito. Un refetch successivo mantiene
  // `dati` con l'ultimo valore buono e usa `ricaricando` per i pulsanti, mai
  // per smontare questo ramo (review controllore, grave 1).
  if (dati === null) {
    if (ricaricando) {
      return <p className="p-6 text-sm text-muted-foreground">Caricamento...</p>;
    }
    return (
      <div className="p-6 space-y-4">
        <Button variant="ghost" size="sm" onClick={() => tornaElenco()}>
          <ArrowLeft className="w-4 h-4" />
          Indietro
        </Button>
        <p className="text-sm text-destructive">{error ?? 'Contratto non trovato'}</p>
      </div>
    );
  }

  const ePassivo = dati.direzione === 'passivo';

  // "Conferma il rinnovo": visibile solo a scadenza gia' passata (rinnovo
  // tacito gia' avvenuto di fatto) con rinnovo_auto acceso (Step 2 del brief).
  const rinnovoConfermabile =
    dati.rinnovo_auto && dati.data_fine != null && dati.data_fine < oggiISO();

  // La disdetta CORRENTE (I10): se ne esiste gia' una per il ciclo in corso
  // (stessa scadenza sorvegliata di data_fine), il pulsante diventa
  // un'etichetta - un secondo tentativo darebbe 422, non serve provarci.
  const disdettaCorrente = dati.disdette.find((d) => d.scadenza_sorvegliata === dati.data_fine);

  // M8: quanto distrugge l'annullamento, dal dettaglio gia' a schermo. Le
  // scadenze che l'annullamento tocca sono quelle collegate ai periodi (solo
  // sui passivi ce ne sono): sugli attivi senza periodi la cifra e' zero, e
  // annulla_contratto rifiuterebbe comunque se un periodo fosse gia'
  // agganciato a una fattura. Un periodo scartato o fallito non ha prodotto
  // nessun effetto vivo (nessuna fattura, nessuna scadenza aperta): contarlo
  // fra cio' che l'annullamento "distrugge" sovrastimerebbe l'impatto
  // (review controllore 2026-08-03, medio 9).
  const periodiAnnullabili = dati.periodi.filter(
    (p) => p.stato !== 'scartato' && p.stato !== 'fallito',
  );
  const importoAnnullamentoCent = sommaCentesimi(
    periodiAnnullabili.map((p) => p.scadenza?.importo ?? p.totale),
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Button variant="ghost" size="sm" onClick={() => tornaElenco()}>
            <ArrowLeft className="w-4 h-4" />
            Indietro
          </Button>
          <h1 className="text-2xl font-semibold mt-2">
            {dati.codice ?? 'Contratto'}{' '}
            <span className="text-muted-foreground">{dati.oggetto}</span>
          </h1>
          {/* <div>, non <p>: Badge renderizza un <div> ed e' HTML non valido
              annidarlo in un <p> (review controllore 2026-08-03, minore 13). */}
          <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-2">
            <Badge variant={ePassivo ? 'secondary' : 'default'}>
              {ePassivo ? 'Passivo' : 'Attivo'}
            </Badge>
            {dati.ragione_sociale ?? VUOTO} - {dati.tipo} - decorrenza {fmtDate(dati.data_inizio)}
            {dati.data_fine ? ` - scadenza ${fmtDate(dati.data_fine)}` : ''}
          </div>
        </div>
        {/* B5/d: sui passivi niente Genera - il backend lo rifiuta comunque
            (Task 18), e non c'e' ancora nient'altro da mettere al loro posto
            ("Estendi le scadenze" e' a backlog, P5). C7: dietro
            contratti.gestione, come il backend (contratti.py:1127). "Prova
            senza scrivere niente" (ex Simula) e' SPOSTATO dentro il
            riquadro "Cosa succedera' da solo" qui sotto (Task 9). */}
        {!ePassivo && puoGestire && (
          <div className="flex items-center gap-2">
            <Button type="button" onClick={() => void genera()} disabled={inCorso !== null || ricaricando}>
              <Play className="w-4 h-4" />
              Genera proposta
            </Button>
            <AiutoCampo
              campo="genera proposta"
              testo="Propone il primo periodo concluso e non ancora coperto: non emette ancora una fattura, resta da confermare."
            />
          </div>
        )}
      </div>

      {error && (
        <div role="alert" className="flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          {error}
        </div>
      )}
      {avviso && (
        <div className="p-3 rounded-md bg-primary/10 text-primary text-sm">{avviso}</div>
      )}

      {dati.ricorrenza && (
        <Card>
          <CardHeader>
            <CardTitle>Ripetizione</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <p>
              {capitalizza(dati.ricorrenza.periodicita)}, ancorata al giorno{' '}
              {dati.ricorrenza.ancora_giorno} a partire dal{' '}
              {fmtDate(dati.ricorrenza.data_ancora)}. Conteggio:{' '}
              {dati.ricorrenza.sorgente_quantita_codice}.
            </p>
            {/* "Generazione automatica attiva/ferma" basato SOLO su
                ricorrenza.attiva era il bug P2 del referto dali: falso
                quando l'ambiente o l'azienda sono spenti. Sostituito dal
                riquadro "Cosa succedera' da solo" qui sotto, che guarda
                tutti e tre gli interruttori (Task 9). */}
          </CardContent>
        </Card>
      )}

      {/* --- "Cosa succedera' da solo" (Task 9, referto dali DETTAGLIO b):
          il riquadro della fiducia. Solo sui contratti ATTIVI e solo se il
          contratto ha una ricorrenza (senza, non c'e' niente da accendere).
          --- */}
      {!ePassivo && dati.ricorrenza && (
        <Card>
          <CardHeader>
            <CardTitle>Cosa succedera' da solo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-3">
              <div>
                <dt className="text-muted-foreground inline-flex items-center gap-1">
                  Ambiente
                  <AiutoCampo
                    campo="interruttore ambiente"
                    testo="Vale per l'intera installazione: si accende in configurazione, non da questa pagina."
                  />
                </dt>
                <dd className="font-medium">
                  {statoGen ? (statoGen.ambiente ? 'Accesa' : 'Ferma') : '...'}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground inline-flex items-center gap-1">
                  Azienda
                  <AiutoCampo
                    campo="interruttore azienda"
                    testo="Vale per tutti i contratti dell'azienda: si accende in configurazione, non da questa pagina."
                  />
                </dt>
                <dd className="font-medium">
                  {statoGen ? (statoGen.tenant ? 'Accesa' : 'Ferma') : '...'}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Fatturazione automatica</dt>
                <dd className="flex items-center gap-2">
                  {/* Interruttore per contratto: PATCH ricorrenza_attiva,
                      NESSUN dialogo (referto dali). Pattern gia' in uso
                      nel prodotto (modules/email RegolePage.tsx). */}
                  <button
                    type="button"
                    role="switch"
                    aria-checked={dati.ricorrenza.attiva}
                    aria-label={`Fatturazione automatica ${dati.ricorrenza.attiva ? 'accesa' : 'ferma'}`}
                    disabled={!puoGestire || alternandoRicorrenza || ricaricando}
                    onClick={() => void alternaRicorrenza()}
                    className={`relative h-5 w-9 rounded-full transition-colors disabled:opacity-50 ${
                      dati.ricorrenza.attiva ? 'bg-primary' : 'bg-muted-foreground/30'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
                        dati.ricorrenza.attiva ? 'left-[18px]' : 'left-0.5'
                      }`}
                    />
                  </button>
                  <span className="font-medium">{dati.ricorrenza.attiva ? 'Accesa' : 'Ferma'}</span>
                </dd>
              </div>
            </dl>

            {erroreStatoGen && <p className="text-xs text-destructive">{erroreStatoGen}</p>}
            {erroreRicorrenza && (
              <p role="alert" className="text-xs text-destructive">{erroreRicorrenza}</p>
            )}

            {/* Frase onesta sotto l'interruttore: mai dire che sta per
                partire qualcosa se l'ambiente o l'azienda sono spenti
                (microcopy esatta del referto dali). Due casi distinti,
                mai lo stesso testo (review round 1): l'ambiente e' PIU' A
                MONTE del tenant (generazione_auto.py:209-211 esce subito se
                l'ambiente e' spento, prima ancora di leggere la chiave
                tenant) - se e' spento lui, dirlo per nome invece di dire
                "azienda", che contraddirebbe la riga di stato "Azienda:
                Accesa" appena sopra. Nessuna delle due leve si aziona da
                qui: nessun pulsante da promettere in nessuno dei due casi. */}
            {dati.ricorrenza.attiva && statoGen && (
              !statoGen.ambiente ? (
                <p className="rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-400 px-3 py-2">
                  La fatturazione automatica e' spenta a livello di installazione: questo
                  contratto non produrra' nulla finche' non viene accesa.
                </p>
              ) : !statoGen.tenant ? (
                <p className="rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-400 px-3 py-2">
                  La fatturazione automatica e' spenta per tutta l'azienda: questo contratto non
                  produrra' nulla finche' non la accendi.
                </p>
              ) : anteprimaPeriodi && anteprimaPeriodi.length > 0 ? (
                <p className="rounded-md bg-primary/10 text-primary px-3 py-2">
                  Al prossimo controllo l'ERP preparera' il canone di{' '}
                  {fmtDate(anteprimaPeriodi[0].periodo_inizio)} -{' '}
                  {fmtDate(anteprimaPeriodi[0].periodo_fine)} per{' '}
                  {fmtEuro(anteprimaPeriodi[0].totale)}. Nessuna fattura parte senza la tua
                  conferma.
                </p>
              ) : null
            )}

            {dati.ricorrenza.ultimo_errore && (
              <p className="text-destructive">{dati.ricorrenza.ultimo_errore}</p>
            )}

            {statoGen && statoGen.canoni_scoperti > 0 && (
              <p className="text-muted-foreground">
                Canoni non ancora fatturati:{' '}
                <span className="font-medium tabular-nums">{statoGen.canoni_scoperti}</span>
              </p>
            )}

            {/* "Prova senza scrivere niente" (ex Simula), SPOSTATO qui
                dentro il riquadro (Task 9). */}
            <div className="flex items-center gap-2 pt-1 border-t border-border">
              {puoGestire && (
                <Button
                  type="button"
                  variant="outline"
                  className="mt-3"
                  onClick={() => void simula()}
                  disabled={inCorso !== null || ricaricando}
                >
                  <FlaskConical className="w-4 h-4" />
                  Prova senza scrivere niente
                </Button>
              )}
              <div className="mt-3">
                <AiutoCampo
                  campo="prova senza scrivere niente"
                  testo="Calcola cosa uscirebbe dalla prossima generazione, senza scrivere niente: nessun periodo occupato, nessuna proposta creata."
                />
              </div>
            </div>
            {simulazione && (
              <div className="p-3 rounded-md bg-primary/5 text-foreground text-sm space-y-1">
                {simulazione.stato === 'simulato' ? (
                  <p>
                    Uscirebbe il canone {fmtDate(simulazione.periodo_inizio)} -{' '}
                    {fmtDate(simulazione.periodo_fine)} per {fmtEuro(simulazione.totale)}. Non ho
                    scritto nulla.
                  </p>
                ) : (
                  <p>Nessuna proposta: {simulazione.messaggio ?? simulazione.stato}.</p>
                )}
              </div>
            )}

            {/* Prossimi canoni: GET /anteprima, calcolata sui dati salvati,
                PURA (P5 referto dali). */}
            <div>
              <h3 className="text-xs font-medium uppercase text-muted-foreground mb-1">
                Prossimi canoni
              </h3>
              {anteprimaAConsumo ? (
                <p className="text-muted-foreground">
                  Questo contratto conta a consumo: la quantita' va registrata prima di poter
                  calcolare i prossimi canoni.
                </p>
              ) : erroreAnteprima ? (
                <p className="text-destructive">{erroreAnteprima}</p>
              ) : anteprimaPeriodi === null ? (
                <p className="text-muted-foreground">Caricamento...</p>
              ) : anteprimaPeriodi.length === 0 ? (
                <p className="text-muted-foreground">Nessun canone ancora da preparare.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Periodo</TableHead>
                      <TableHead className="text-right">Importo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {anteprimaPeriodi.map((p) => (
                      <TableRow key={p.periodo_inizio}>
                        <TableCell className="whitespace-nowrap">
                          {fmtDate(p.periodo_inizio)} - {fmtDate(p.periodo_fine)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{fmtEuro(p.totale)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* --- Rinnovo e disdetta (Step 2 del brief + Task 18) --- */}
      <Card>
        <CardHeader>
          <CardTitle>Rinnovo e disdetta</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {dati.data_fine == null ? (
            <p className="text-muted-foreground">
              Il contratto non ha una scadenza: niente da rinnovare o disdire.
            </p>
          ) : (
            <>
              <dl className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
                <div className="flex justify-between sm:block">
                  <dt className="text-muted-foreground">Scadenza</dt>
                  <dd className="font-medium">{fmtDate(dati.data_fine)}</dd>
                </div>
                <div className="flex justify-between sm:block">
                  <dt className="text-muted-foreground">Rinnovo automatico</dt>
                  <dd className="font-medium">{dati.rinnovo_auto ? 'Si' : 'No'}</dd>
                </div>
                <div className="flex justify-between sm:block">
                  <dt className="text-muted-foreground">Preavviso</dt>
                  <dd className="font-medium">{dati.preavviso_testo ?? 'non indicato'}</dd>
                </div>
                <div className="flex justify-between sm:block">
                  <dt className="text-muted-foreground inline-flex items-center gap-1">
                    Ultimo giorno utile per disdire
                    <AiutoCampo
                      campo="ultimo giorno utile per disdire"
                      testo="Entro questa data va mandata la disdetta (PEC o raccomandata): dopo, il rinnovo tacito e' gia' avvenuto e la scadenza si sposta avanti da sola."
                    />
                  </dt>
                  <dd
                    className={
                      dati.ultimo_giorno_disdetta && giorniAlTermine(dati.ultimo_giorno_disdetta) < 0
                        ? 'font-medium text-destructive'
                        : 'font-medium'
                    }
                  >
                    {dati.ultimo_giorno_disdetta
                      ? descrizioneTermineDisdetta(dati.ultimo_giorno_disdetta)
                      : 'non calcolabile'}
                  </dd>
                </div>
                <div className="flex justify-between sm:block">
                  <dt className="text-muted-foreground inline-flex items-center gap-1">
                    Registrato all'Agenzia delle Entrate
                    <AiutoCampo
                      campo="registrato all'Agenzia delle Entrate"
                      testo="Se registrato, la proroga tacita va comunicata all'Agenzia delle Entrate entro 30 giorni dalla conferma del rinnovo (modello RLI, art. 17 DPR 131/1986)."
                    />
                  </dt>
                  <dd className="font-medium">
                    {dati.registrato ? (dati.estremi_registrazione ?? 'Si') : 'No'}
                  </dd>
                </div>
              </dl>

              {disdettaCorrente && (
                <p className="rounded-md bg-muted px-3 py-2 text-muted-foreground">
                  Disdetta registrata il {fmtDate(disdettaCorrente.data_invio)}
                  {disdettaCorrente.data_ricezione
                    ? `, ricevuta il ${fmtDate(disdettaCorrente.data_ricezione)}`
                    : ''}
                  . Il rinnovo tacito di questo ciclo e' bloccato.
                </p>
              )}

              <div className="flex flex-wrap items-center gap-3 pt-1">
                {puoGestire && rinnovoConfermabile && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void confermaRinnovo()}
                    disabled={inCorso !== null || ricaricando}
                  >
                    Conferma il rinnovo
                  </Button>
                )}
                {puoGestire && !disdettaCorrente && (
                  <Button type="button" variant="outline" onClick={apriDisdetta} disabled={ricaricando}>
                    Registra la disdetta
                  </Button>
                )}
                {erroreRinnovo && (
                  <span role="alert" className="text-xs text-destructive">{erroreRinnovo}</span>
                )}
              </div>
            </>
          )}

          {puoGestire && (
            <div className="border-t border-border pt-3 flex items-center gap-2 flex-wrap">
              {/* "Questo periodo l'abbiamo gia' fatturato" (aggancia-storico,
                  referto dali DETTAGLIO d): solo sugli attivi, la stessa
                  guardia di Simula/Genera (Task 18). Disabilitato se non
                  c'e' niente da agganciare - nessun periodo calcolato o
                  contratto a consumo senza rilevazioni. */}
              {!ePassivo && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={ricaricando || !anteprimaPeriodi || anteprimaPeriodi.length === 0}
                    onClick={apriAggancio}
                  >
                    <Link2 className="w-4 h-4" />
                    Questo periodo l'abbiamo gia' fatturato
                  </Button>
                  <AiutoCampo
                    campo="questo periodo l'abbiamo gia' fatturato"
                    testo="Dichiara che un periodo e' gia' stato fatturato con una fattura reale, gia' esistente: serve prima di accendere la fatturazione automatica su un contratto gia' in corso, per non far riproporre i mesi arretrati."
                  />
                </>
              )}
              <Button
                type="button"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                disabled={ricaricando}
                onClick={() => {
                  setErroreAnnulla(null);
                  setAnnullaApertoOpen(true);
                }}
              >
                <Trash2 className="w-4 h-4" />
                Annulla il contratto
              </Button>
              <AiutoCampo
                campo="annulla il contratto"
                testo="Cancella il contratto se non ha ancora prodotto effetti contabili (nessuna scadenza chiusa, nessuna fattura agganciata). Se li ha gia' prodotti va cessato, non annullato."
              />
            </div>
          )}
        </CardContent>
      </Card>

      <section className="space-y-2">
        <h2 className="font-medium text-base">Voci a canone</h2>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Descrizione</TableHead>
              <TableHead>Quantita</TableHead>
              <TableHead className="text-right">Prezzo</TableHead>
              <TableHead className="text-right">Sconto</TableHead>
              <TableHead className="text-right">IVA</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {dati.righe.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.numero_linea}</TableCell>
                <TableCell>{r.descrizione}</TableCell>
                <TableCell>
                  {r.origine_quantita === 'fissa' ? (r.quantita ?? VUOTO) : 'rilevata'}{' '}
                  {r.unita_misura ?? ''}
                </TableCell>
                <TableCell className="text-right tabular-nums">{fmtEuro(r.prezzo_unitario)}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {r.sconto_percentuale ? `${r.sconto_percentuale}%` : VUOTO}
                </TableCell>
                <TableCell className="text-right tabular-nums">{r.aliquota_iva_percentuale}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </section>

      <section className="space-y-2">
        <h2 className="font-medium text-base">Periodi</h2>
        {dati.periodi.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {ePassivo
              ? 'Nessun periodo ancora aperto.'
              : 'Nessun periodo. Con "Prova senza scrivere niente" si vede cosa uscirebbe senza scrivere niente.'}
          </p>
        ) : (
          // Colonne: Periodo | Stato | Importo | Fattura | Scadenza di
          // incasso | Azioni (referto dali, DETTAGLIO c - ordine esatto,
          // "Imponibile" tolta).
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Periodo</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="text-right">Importo</TableHead>
                <TableHead>Fattura</TableHead>
                <TableHead>Scadenza di incasso</TableHead>
                {!ePassivo && <TableHead>Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {dati.periodi.map((p) => {
                const scaduta =
                  p.scadenza?.stato === 'aperta' && p.scadenza.data < oggiISO();
                return (
                  <TableRow key={p.id}>
                    <TableCell className="whitespace-nowrap align-top">
                      {fmtDate(p.periodo_inizio)} - {fmtDate(p.periodo_fine)}
                    </TableCell>
                    <TableCell className="align-top">
                      <Badge variant={STATO_PERIODO_VARIANT[p.stato] ?? 'muted'}>
                        {STATO_PERIODO_LABEL[p.stato] ?? p.stato}
                      </Badge>
                      {p.note && <p className="text-xs text-muted-foreground mt-1">{p.note}</p>}
                      {p.ultimo_errore && (
                        <p className="text-xs text-destructive mt-1">{p.ultimo_errore}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums align-top">{fmtEuro(p.totale)}</TableCell>
                    <TableCell className="align-top">
                      {/* C5: link a /documentale/fatture/{fattura_id}?year=
                          {fattura_year}. Etichetta "in bozza, da controllare
                          e inviare", MAI "emessa": e' cio' che il periodo
                          sa - se poi la fattura e' stata inviata o
                          accettata, questa pagina non lo vede (referto
                          dali, DETTAGLIO c). */}
                      {p.fattura_id != null && p.fattura_year != null ? (
                        <div>
                          <Link
                            to={`/documentale/fatture/${p.fattura_id}?year=${p.fattura_year}`}
                            className="font-mono text-xs font-medium text-primary hover:underline"
                          >
                            {p.fattura_numero ?? p.fattura_id.slice(0, 8)}
                          </Link>
                          <div className="text-xs text-muted-foreground">
                            in bozza, da controllare e inviare
                          </div>
                        </div>
                      ) : (
                        VUOTO
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      {p.scadenza ? (
                        <div className="whitespace-nowrap">
                          <span className={scaduta ? 'font-medium text-destructive' : ''}>
                            {fmtDate(p.scadenza.data)}
                          </span>{' '}
                          <Badge
                            variant={
                              p.scadenza.stato === 'aperta'
                                ? (scaduta ? 'destructive' : 'default')
                                : 'secondary'
                            }
                          >
                            {p.scadenza.stato === 'aperta' ? (scaduta ? 'Scaduta' : 'Aperta') : 'Chiusa'}
                          </Badge>
                          <div className="text-xs text-muted-foreground tabular-nums">
                            {fmtEuro(p.scadenza.importo)}
                          </div>
                        </div>
                      ) : (
                        VUOTO
                      )}
                    </TableCell>
                    {!ePassivo && (
                      <TableCell className="align-top">
                        {p.stato === 'proposto' ? (
                          <div className="flex gap-2">
                            {/* C7: Emetti dietro contratti.conferma, Scarta
                                dietro contratti.gestione - stesso permesso
                                del backend, nessun pulsante che risponde
                                403. */}
                            {puoConfermare && (
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => apriEmetti(p)}
                                disabled={inCorso !== null || ricaricando}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Emetti la fattura
                              </Button>
                            )}
                            {puoGestire && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => apriScarta(p)}
                                disabled={inCorso !== null || ricaricando}
                              >
                                <XCircle className="w-3.5 h-3.5" />
                                Non fatturare questo periodo
                              </Button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">nessuna</span>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </section>

      {/* --- Dialogo emissione fattura (C6): sostituisce il window.confirm,
          testo esatto del referto dali. Chiude il debito pre-esistente
          dichiarato nel commento in testa al file. --- */}
      <Dialog
        open={periodoDaEmettere !== null}
        onOpenChange={(open) => {
          if (!open) setPeriodoDaEmettere(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {periodoDaEmettere &&
                `Emettere la fattura per ${fmtDate(periodoDaEmettere.periodo_inizio)} - ${fmtDate(periodoDaEmettere.periodo_fine)}?`}
            </DialogTitle>
            <DialogDescription>
              Nasce una fattura in bozza con un numero progressivo. Il numero non si restituisce:
              se poi la fattura non serve, va annullata con una nota di credito.
            </DialogDescription>
          </DialogHeader>
          {periodoDaEmettere && (
            <dl className="text-sm">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Importo</dt>
                <dd className="font-medium tabular-nums">{fmtEuro(periodoDaEmettere.totale)}</dd>
              </div>
            </dl>
          )}
          {erroreEmetti && (
            <div role="alert" className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <AlertTriangle className="w-4 h-4" />
              {erroreEmetti}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPeriodoDaEmettere(null)}>
              Annulla
            </Button>
            <Button onClick={() => void confermaEmissione()} disabled={inCorso !== null}>
              {inCorso === periodoDaEmettere?.id ? 'Emissione...' : 'Emetti la fattura'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Dialogo scarto periodo (round 2, punto 4 rifatto): niente piu'
          window.prompt. "Annulla" nel Dialog = rinuncia, silenziosa; submit
          col campo vuoto = validazione inline, non un'eccezione. --- */}
      <Dialog
        open={periodoDaScartare !== null}
        onOpenChange={(open) => {
          if (!open) setPeriodoDaScartare(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Scarta il periodo</DialogTitle>
            <DialogDescription>
              {periodoDaScartare && (
                <>
                  {fmtDate(periodoDaScartare.periodo_inizio)} -{' '}
                  {fmtDate(periodoDaScartare.periodo_fine)}: il motivo resta scritto sul
                  contratto, il periodo non verra' piu' riproposto.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="scarta-motivo">Motivo</Label>
              <textarea
                id="scarta-motivo"
                rows={3}
                value={scartaMotivo}
                onChange={(e) => setScartaMotivo(e.target.value)}
                placeholder="Perche' questo periodo non va fatturato?"
                className="flex w-full rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
              />
            </div>
            {erroreScarta && (
              <div role="alert" className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                <AlertTriangle className="w-4 h-4" />
                {erroreScarta}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPeriodoDaScartare(null)}>
              Annulla
            </Button>
            <Button
              variant="outline"
              onClick={() => void confermaScarta()}
              disabled={inCorso !== null || !scartaMotivo.trim()}
            >
              {inCorso === periodoDaScartare?.id ? 'Scarto...' : 'Conferma lo scarto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Dialogo disdetta (I11: niente PEC) --- */}
      <Dialog open={disdettaApertaOpen} onOpenChange={setDisdettaApertaOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registra la disdetta</DialogTitle>
            <DialogDescription>
              Registra che la disdetta e' gia' stata mandata (PEC o raccomandata): non ne invia una nuova.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="disdetta-invio">Data di invio</Label>
              <Input
                id="disdetta-invio"
                type="date"
                value={disdettaDataInvio}
                onChange={(e) => setDisdettaDataInvio(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="disdetta-ricezione">Data di ricezione (facoltativa)</Label>
              <Input
                id="disdetta-ricezione"
                type="date"
                value={disdettaDataRicezione}
                onChange={(e) => setDisdettaDataRicezione(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="disdetta-note">Note (facoltative)</Label>
              <textarea
                id="disdetta-note"
                rows={3}
                value={disdettaNote}
                onChange={(e) => setDisdettaNote(e.target.value)}
                className="flex w-full rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
              />
            </div>
            {erroreDisdetta && (
              <div role="alert" className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                <AlertTriangle className="w-4 h-4" />
                {erroreDisdetta}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDisdettaApertaOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={() => void registraDisdetta()}
              disabled={salvandoDisdetta || !disdettaDataInvio}
            >
              {salvandoDisdetta ? 'Registrazione...' : 'Registra la disdetta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Dialogo annullamento: l'unica conferma esplicita (M7/M8) --- */}
      <Dialog open={annullaApertoOpen} onOpenChange={setAnnullaApertoOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Annullare il contratto?</DialogTitle>
            <DialogDescription>
              {periodiAnnullabili.length > 0 ? (
                <>
                  Vengono annullati {periodiAnnullabili.length} period
                  {periodiAnnullabili.length === 1 ? 'o' : 'i'} e le relative scadenze, per un
                  totale di{' '}
                  <strong className="tabular-nums">{centesimiInEuro(importoAnnullamentoCent)}</strong>.
                  {dati.periodi.length > periodiAnnullabili.length && (
                    <>
                      {' '}
                      (
                      {dati.periodi.length - periodiAnnullabili.length === 1
                        ? 'un altro periodo gia\' scartato o fallito'
                        : `altri ${dati.periodi.length - periodiAnnullabili.length} gia' scartati o falliti`}
                      , senza effetto: non cont
                      {dati.periodi.length - periodiAnnullabili.length === 1 ? 'a' : 'ano'} nel totale)
                    </>
                  )}{' '}
                  E' consentito solo se nessuna scadenza risulta gia' chiusa e nessun periodo e'
                  agganciato a una fattura: in quel caso il contratto va cessato, non annullato.
                </>
              ) : (
                'Il contratto non ha ancora periodi con effetto: l\'annullamento non tocca nessuna scadenza.'
              )}
            </DialogDescription>
          </DialogHeader>
          {erroreAnnulla && (
            <div role="alert" className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <AlertTriangle className="w-4 h-4" />
              {erroreAnnulla}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAnnullaApertoOpen(false)}>
              Resta
            </Button>
            <Button
              variant="destructive"
              onClick={() => void annullaContratto()}
              // || ricaricando (round 2, grave 3): senza, si poteva confermare
              // l'annullamento mentre un refetch stava sostituendo `dati` sotto
              // al dialogo gia' aperto (conteggio/importo del Dialog calcolati
              // sul valore vecchio).
              disabled={annullando || ricaricando}
            >
              {annullando ? 'Annullamento...' : 'Annulla il contratto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Dialogo aggancia-storico (referto dali, DETTAGLIO d): il
          periodo si sceglie dal calcolo (Select su anteprimaPeriodi), la
          fattura si cerca (Combobox server-search) - MAI una data digitata
          a mano. --- */}
      <Dialog open={aggancioOpen} onOpenChange={setAggancioOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Questo periodo l'abbiamo gia' fatturato</DialogTitle>
            <DialogDescription>
              Collega un periodo gia' fatturato in passato alla fattura reale che lo copre: il
              periodo risultera' fatturato senza che l'ERP ne emetta una nuova.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="aggancio-periodo">Periodo</Label>
              <Select
                value={periodoAggancio ? periodoAggancio.periodo_inizio : ''}
                onValueChange={(v) => {
                  const trovato = (anteprimaPeriodi ?? []).find((p) => p.periodo_inizio === v) ?? null;
                  setPeriodoAggancio(trovato);
                  setFatturaSelezionata(null);
                  setFatturaQuery('');
                }}
              >
                <SelectTrigger id="aggancio-periodo">
                  <SelectValue placeholder="Scegli il periodo" />
                </SelectTrigger>
                <SelectContent>
                  {(anteprimaPeriodi ?? []).map((p) => (
                    <SelectItem key={p.periodo_inizio} value={p.periodo_inizio}>
                      {fmtDate(p.periodo_inizio)} - {fmtDate(p.periodo_fine)} ({fmtEuro(p.totale)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="aggancio-fattura">Fattura</Label>
              <Combobox
                id="aggancio-fattura"
                value={fatturaQuery}
                loading={cercandoFatture}
                disabled={!periodoAggancio}
                onInputChange={(v) => {
                  setFatturaQuery(v);
                  if (fatturaSelezionata) setFatturaSelezionata(null);
                }}
                onSelect={(o) => {
                  const trovata = fatturaOpzioni.find((f) => f.id === o.value) ?? null;
                  setFatturaSelezionata(trovata);
                  setFatturaQuery(o.label);
                }}
                options={fatturaOpzioni.map((f) => ({
                  value: f.id,
                  label: f.numero ?? f.id.slice(0, 8),
                  sublabel:
                    `${fmtDate(f.data)} - ${f.cessionario_denominazione ?? VUOTO} - ` +
                    fmtEuro(f.importo_totale != null ? String(f.importo_totale) : null),
                }))}
                placeholder="Cerca fattura per numero o cliente..."
              />
              {erroreFatture && (
                <p role="alert" className="mt-1 text-xs text-destructive">{erroreFatture}</p>
              )}
            </div>
            {erroreAggancio && (
              <div role="alert" className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                <AlertTriangle className="w-4 h-4" />
                {erroreAggancio}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAggancioOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={() => void confermaAggancio()}
              disabled={salvandoAggancio || !periodoAggancio || !fatturaSelezionata}
            >
              {salvandoAggancio ? 'Aggancio...' : "Conferma l'aggancio"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContrattoDetail;
